import { useEffect, useMemo, useState } from "react";
import { verReporteIndividual } from "../../../utils/reports";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import CronogramaForm from "../../../components/ui/CronogramaForm";
import {
  Wrench, LoaderPinwheel, Search, ChevronLeft, ChevronRight,
  CalendarDays, Pencil, Trash2, Eye, CalendarX,
} from "lucide-react";
import PageSizeSelect from "../../../components/ui/PageSizeSelect";

const TiposMantenimiento = ["Preventivo", "Correctivo", "Calibración"];
const Estados = ["Programado", "Pendiente", "Completado", "Cancelado"];

const ESTADO_BADGE = {
  Pendiente:  "bg-amber-50 text-amber-700 border border-amber-200",
  Programado: "bg-blue-50 text-blue-700 border border-blue-200",
  Completado: "bg-green-50 text-green-700 border border-green-200",
  Cerrado:    "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Cancelado:  "bg-red-50 text-red-700 border border-red-200",
};

const TIPO_BADGE = {
  Preventivo:    "bg-blue-50 text-blue-700 border border-blue-200",
  Correctivo:    "bg-orange-50 text-orange-700 border border-orange-200",
  "Calibración": "bg-violet-50 text-violet-700 border border-violet-200",
};

const MantenimientosEquipo = ({
  equipoId,
  publicView = false,
  initialMantenimientos = [],
}) => {
  const { id } = useParams();
  const currentEquipoId = equipoId || id;
  const [servicios, setServicios] = useState(initialMantenimientos || []);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  const fetchServicios = async (opts = {}) => {
    if (publicView) return;
    setLoading(true);
    try {
      const params = {
        search: opts.search ?? search,
        tipo: opts.tipo ?? filterTipo,
        estado: opts.estado ?? filterEstado,
      };
      const res = await axios.get(
        `${API}/cronograma/equipo/${currentEquipoId}`,
        { headers: { Authorization: `Bearer ${token}` }, params },
      );
      setServicios(res.data);
      setPage(1);
    } catch {
      toast.error("Error al cargar mantenimientos del equipo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (publicView) {
      setServicios(initialMantenimientos || []);
      return;
    }
    if (currentEquipoId) fetchServicios();
    // eslint-disable-next-line
  }, [currentEquipoId, publicView, JSON.stringify(initialMantenimientos)]);

  const handleSearch = () => fetchServicios({ search });
  const handleFilterTipo = (value) => {
    setFilterTipo(value);
    fetchServicios({ tipo: value });
  };
  const handleFilterEstado = (value) => {
    setFilterEstado(value);
    fetchServicios({ estado: value });
  };

  const handleEdit = (serv) => {
    if (publicView) return;
    setEditing(serv);
    setShowForm(true);
  };

  const handleDelete = async (idToDelete) => {
    if (publicView) return;
    if (!window.confirm("¿Eliminar este mantenimiento?")) return;
    try {
      await axios.delete(`${API}/cronograma/${idToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.info("Mantenimiento eliminado");
      fetchServicios();
    } catch {
      toast.error("Error al eliminar mantenimiento");
    }
  };

  const handleSave = async (payload) => {
    if (publicView) return;
    try {
      if (editing) {
        await axios.put(`${API}/cronograma/${editing._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Mantenimiento actualizado");
      } else {
        await axios.post(
          `${API}/cronograma`,
          { ...payload, equipo: currentEquipoId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("Mantenimiento creado");
      }
      setShowForm(false);
      setEditing(null);
      fetchServicios();
    } catch {
      toast.error("Error al guardar mantenimiento");
    }
  };

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return servicios.slice(start, start + pageSize);
  }, [servicios, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(servicios.length / pageSize));

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center gap-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50">
          <Wrench className="h-4 w-4 text-orange-500" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-800">
            Mantenimientos del equipo
          </h3>
          <p className="text-xs text-gray-400">
            {servicios.length} registro{servicios.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {loading && (
        <div className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm text-blue-700">
          <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
          Cargando datos…
        </div>
      )}

      {/* ── Filters ── */}
      {!publicView && (
        <div className="flex flex-col sm:flex-row flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Buscar por observación o tipo…"
              className="border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm w-64 focus:ring-2 focus:ring-blue-400 focus:outline-none"
            />
          </div>
          <select
            value={filterTipo}
            onChange={(e) => handleFilterTipo(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
          >
            <option value="">Todos los tipos</option>
            {TiposMantenimiento.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            value={filterEstado}
            onChange={(e) => handleFilterEstado(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
          >
            <option value="">Todos los estados</option>
            {Estados.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>
      )}

      {/* ── Table ── */}
      <div className="flex items-center justify-end mb-2">
        <PageSizeSelect
          value={pageSize}
          onChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha programada</th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Responsable</th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Observaciones</th>
              <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 bg-white">
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-gray-400">
                  Cargando…
                </td>
              </tr>
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-12 text-center">
                  <CalendarX className="h-9 w-9 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Sin mantenimientos registrados</p>
                </td>
              </tr>
            ) : (
              paged.map((s) => (
                <tr key={s._id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                        TIPO_BADGE[s.tipo] || "bg-gray-100 text-gray-600 border-gray-200"
                      }`}
                    >
                      {s.tipo || "—"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      {new Date(s.fechaProgramada).toLocaleDateString("es-CO")}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                        ESTADO_BADGE[s.estado] || "bg-gray-100 text-gray-600 border-gray-200"
                      }`}
                    >
                      {s.estado || "—"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">
                    {s.personaAsignada ? s.personaAsignada.name : (
                      <span className="text-gray-400 italic">Sin asignar</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-500 max-w-48 truncate">
                    {s.observaciones || "—"}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {publicView ? (
                      <span className="text-xs text-gray-400 italic">Solo lectura</span>
                    ) : s.estado === "Programado" ? (
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          onClick={() => handleEdit(s)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(s._id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ) : s.estado === "Completado" ? (
                      <button
                        onClick={() =>
                          navigate(`/manage/${s._id}`, { state: { servicio: s } })
                        }
                        className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Ver"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    ) : s.estado === "Cerrado" ? (
                      <button
                        onClick={() => verReporteIndividual(s._id)}
                        className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Ver acta"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No editable</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          {servicios.length === 0
            ? "Sin resultados"
            : `${(page - 1) * pageSize + 1}–${Math.min(
                page * pageSize,
                servicios.length,
              )} de ${servicios.length}`}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-2 tabular-nums">{page} / {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!publicView && showForm && (
        <CronogramaForm
          mantenimiento={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default MantenimientosEquipo;
